"use client";

import React, { useEffect, useMemo, useState } from "react";
import { FileText, Banknote, Printer } from "lucide-react";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import OrderItemCard from "../../components/OrderItemCard";
import { getReceiptDetails as apiGetReceiptDetails } from "../../../../../api/frontShop/ReceiptAPI";
import { listRefundedOrders as apiListRefundedOrders } from "../../../../../api/frontShop/RefundsAPI";
import ReceiptBarcode from "../../../../../components/ReceiptBarcode";
import PrintReceiptDialog from "../../../../../components/print/views/PrintReceiptDialog";
import styles from "../css/cardOrderDetail.module.css";

export default function CardOrderDetailContent({ navigate, paymentMethod = "Card", recieptno }) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [receipt, setReceipt] = useState(null);
	const [printOpen, setPrintOpen] = useState(false);
	const [refundedReceiptNos, setRefundedReceiptNos] = useState(() => new Set());

	useEffect(() => {
		let cancelled = false;
		async function load() {
			if (!recieptno) {
				setError("No receipt number provided");
				return;
			}
			setLoading(true);
			setError("");
			try {
				const [{ data: json }, { data: refundedJson }] = await Promise.all([
					apiGetReceiptDetails(recieptno),
					apiListRefundedOrders(),
				]);
				const recieptnos = Array.isArray(refundedJson?.data?.recieptnos) ? refundedJson.data.recieptnos : [];
				const set = new Set(recieptnos.map((s) => String(s || "").trim()).filter((s) => s));
				if (!cancelled) {
					setReceipt(json?.data || null);
					setRefundedReceiptNos(set);
				}
			} catch (e) {
				if (!cancelled) setError(e?.message || "Failed to load receipt");
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [recieptno]);

	const displayPaymentName = useMemo(() => {
		const method = receipt?.payment?.paymentMethod || paymentMethod || "Card";
		return method === "Ikhokha" ? "iKhokha" : method;
	}, [paymentMethod, receipt]);

	const customerName = useMemo(() => {
		const first = receipt?.customer?.firstname || "";
		const last = receipt?.customer?.surname || "";
		return `${first} ${last}`.trim();
	}, [receipt]);

	const completedAt = useMemo(() => {
		if (!receipt?.completedAt) return null;
		const d = new Date(receipt.completedAt);
		return Number.isNaN(d.getTime()) ? null : d;
	}, [receipt]);

	const dateLine = useMemo(() => {
		if (!completedAt) return "";
		const options = { weekday: "long", month: "long", day: "numeric", year: "numeric" };
		const datePart = completedAt.toLocaleDateString(undefined, options);
		const timePart = completedAt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
		return `${datePart} at ${timePart}`;
	}, [completedAt]);

	const refundReason = receipt?.refund?.refundreason != null ? String(receipt.refund.refundreason) : "";
	const saleProcessedBy = receipt?.processedBy != null ? String(receipt.processedBy) : "";
	const refundProcessedBy = receipt?.refund?.processedBy != null ? String(receipt.refund.processedBy) : "";
	const refundApprovedBy = receipt?.refund?.approvedBy != null ? String(receipt.refund.approvedBy) : "";

	const totalAmount = Number(receipt?.totals?.totalamount || 0);
	const items = Array.isArray(receipt?.items) ? receipt.items : [];

	const isRefunded = useMemo(() => {
		const r = String(receipt?.recieptno || recieptno || "").trim();
		if (!r) return false;
		return refundedReceiptNos.has(r);
	}, [receipt?.recieptno, recieptno, refundedReceiptNos]);

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={() => navigate("/frontshop/orders")} />
      <main className={styles.main}>
        <div className={styles.card}>
          <header className={styles.headerTop}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, width: '100%' }}>
              <div>
                <h1 className={styles.receiptId}>{receipt?.recieptno || ""}</h1>
                <p className={styles.dateLine}>{dateLine}</p>
              </div>
              <ReceiptBarcode
                className={styles.barcodeWrap}
                value={receipt?.recieptno || ""}
                text={receipt?.recieptno || ""}
                height={60}
                style={{ marginTop: -20 }}
              />
            </div>
          </header>

          <div className={styles.line} />

          <div className={styles.bodyRow}>
            <section className={styles.leftCol}>
              <div className={styles.paidTagWrapper}>
                <span className={`${styles.status} ${styles.paid}`}>Paid</span>
              </div>

              <div className={styles.fulfilledTagWrapper}>
                {isRefunded ? (
                  <span className={`${styles.status} ${styles.refunded}`}>Refunded</span>
                ) : (
                  <span className={`${styles.status} ${styles.fulfilled}`}>Fulfilled</span>
                )}
              </div>

              <div className={styles.customerCard}>
                <p className={styles.customerLabel}>Customer</p>
                <p className={styles.customerName}>{customerName || ""}</p>
              </div>

              <div className={styles.itemsHeader}>Items purchased</div>
              <div className={styles.itemsContainer}>
                <div className={styles.itemsList}>
                  {error && <div className={styles.dateLine}>{error}</div>}
                  {!error && loading && <div className={styles.dateLine}>Loading...</div>}
                  {!error && !loading && items.map((item) => (
                    <OrderItemCard
                      key={item.sku}
                      name={item.name}
                      qtyLabel={`Qty: ${Number(item.qty || 0)} x R ${Number(item.unitPrice || 0).toFixed(2)}`}
                      amount={Number(item.lineTotal || 0)}
                    />
                  ))}
                </div>
              </div>
            </section>

            <section className={styles.rightCol}>
              <div className={styles.paymentMethodCard}>
                <div className={styles.paymentDetailsHeader}>
                  <div className={styles.paymentHeaderLeft}>
                    <FileText className={styles.paymentDetailsIcon} />
                    <span className={styles.paymentDetailsText}>Payment Details</span>
                  </div>
                  <span className={styles.paymentHeaderMethod}>{displayPaymentName}</span>
                </div>

                <div className={styles.paymentBox}>
                  <span className={styles.paymentMethodName}>{displayPaymentName}</span>
                  <span className={styles.paymentMethodAmount}>R {totalAmount.toFixed(2)}</span>
                </div>

                {isRefunded && (
                  <div className={styles.refundReasonBox}>
                    <span className={styles.paymentMethodName}>Refund Reason</span>
                    <span className={styles.refundReasonValue}>{refundReason || "-"}</span>
                  </div>
                )}

                <div className={styles.processedByBox} style={{ top: isRefunded ? 200 : 130 }}>
                  <div className={styles.processedByRow}>
                    <div className={styles.processedByLabels}>
                      <div className={styles.paymentMethodName}>Processed By</div>
                      {isRefunded ? <div className={styles.paymentMethodName}>Approved By</div> : null}
                    </div>
                    <div className={styles.processedByValues}>
                      <div className={styles.refundReasonValue}>{(isRefunded ? refundProcessedBy : saleProcessedBy) || "-"}</div>
                      {isRefunded ? <div className={styles.refundReasonValue}>{refundApprovedBy || "-"}</div> : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.totalCard}>
                <span className={styles.totalLabel}>Total Amount</span>
                <span className={styles.totalValue}>R {totalAmount.toFixed(2)}</span>
              </div>

            </section>
          </div>

          <div className={styles.bottomLine} />

          <footer className={`${styles.footerRow} ${isRefunded ? styles.footerRowCentered : ""}`}>
            <PrintReceiptDialog
              open={printOpen}
              recieptno={receipt?.recieptno || recieptno}
              onClose={() => setPrintOpen(false)}
            />
            {!isRefunded && (
              <Button
                variant="danger"
                className={styles.refundBtn}
                onClick={() =>
                  navigate("/frontshop/payment-types/cod/refund-items", {
                    state: {
                      paymentMethod: "Card",
                      fromPath: "/frontshop/orders/card",
                      items: [],
                      recieptno: receipt?.recieptno,
                    },
                  })
                }
              >
                <span className={styles.buttonContent}>
                  <Banknote className={styles.refundIcon} />
                  <span>Refund</span>
                </span>
              </Button>
            )}
            <Button variant="primary" className={styles.printBtn} onClick={() => setPrintOpen(true)}>
              <span className={styles.buttonContent}>
                <Printer className={styles.printIcon} />
                <span>Print Receipt</span>
              </span>
            </Button>
          </footer>
        </div>
      </main>
      <Footer />
    </div>
  );
}
